/**
 * Who may download a track's file (N12).
 *
 * A download is the clean file through the signed-in audio path (`/api/tracks/signed-audio`), which
 * the backend gives to FULLY SET-UP accounts only: a sign-up that was never finished is refused
 * `account_incomplete`, and one that is pending approval, rejected or suspended
 * `account_access_blocked`. Everyone else already hears the whole track from the public preview, so
 * the Download control is the only thing those accounts lose.
 *
 *   loading   who is looking is not known yet: the control is disabled (never default-allow)
 *   sign-in   signed out: the control asks them to sign in, and the download runs once they have
 *   allowed   a fully set-up account
 *   refused   any other signed-in account: disabled, with the reason as a plain sentence
 */
import type { ViewerAccessState } from "@/hooks/useViewerAccess";
import type { AccountRefusal } from "@/lib/backend-signed-urls";

export type DownloadAccess =
  | { state: "loading"; reason: null }
  | { state: "sign-in"; reason: null }
  | { state: "allowed"; reason: null }
  | { state: "refused"; reason: string };

export const DOWNLOAD_NEEDS_SET_UP = "Finish setting up your account to download tracks.";
export const DOWNLOAD_AWAITS_APPROVAL = "Downloads open once your account is approved.";
export const DOWNLOAD_NOT_AVAILABLE = "This account can't download tracks.";

/** The title of the toast that carries one of the sentences above. */
export const DOWNLOAD_REFUSED_TITLE = "Download not available";

export function downloadAccessFor(access: ViewerAccessState): DownloadAccess {
  switch (access) {
    case "set-up":
      return { state: "allowed", reason: null };
    case "signed-out":
      return { state: "sign-in", reason: null };
    case "incomplete":
      return { state: "refused", reason: DOWNLOAD_NEEDS_SET_UP };
    case "pending":
      return { state: "refused", reason: DOWNLOAD_AWAITS_APPROVAL };
    case "blocked":
      return { state: "refused", reason: DOWNLOAD_NOT_AVAILABLE };
    default:
      return { state: "loading", reason: null };
  }
}

/**
 * The sentence for the backend's refusal: when the account changed after the page knew it, or
 * right after signing in (the pending download runs before the page knows the account). A buyer
 * waiting on approval reads the same sentence as on the page (review Q8-14), not "can't download".
 */
export function downloadRefusalSentence(refusal: AccountRefusal): string {
  switch (refusal) {
    case "account_incomplete":
      return DOWNLOAD_NEEDS_SET_UP;
    case "account_pending_approval":
      return DOWNLOAD_AWAITS_APPROVAL;
    default:
      return DOWNLOAD_NOT_AVAILABLE;
  }
}

/** Props for a Download control: disabled until the account is known, and for a refused one. */
export function downloadControlProps(access: DownloadAccess): { disabled: boolean; title?: string } {
  if (access.state === "loading") return { disabled: true };
  if (access.state === "refused") return { disabled: true, title: access.reason };
  return { disabled: false };
}
